import * as React from "react";
import { useCallback, memo, useEffect, useState, useRef } from "react";
import { getVipList } from "../../api/vip";
import { useGlobalValue } from "../../stores/GlobalState";
import { setGlobalValue } from "../../stores/GlobalState";
import { useUserStore } from "../../modules/user/stores";
import { useT } from "../../i18n/hooks";
import type {
  NavigationItem,
  BottomNavigationProps,
} from "./BottomNavigation.types";
import "./BottomNavigation.scss";
import { LottieIcon } from "./LottieIcon";

// Invite(News) · Events(Games) · Home · Rewards · Account(Menu)
// 使用 Lottie 动画图标
const defaultItems: NavigationItem[] = [
  {
    id: "agent-center",
    name: "Invite",
    icon: "invite",
    useLottie: true,
    badge: "0",
  },
  {
    id: "event",
    name: "Promotions",
    icon: "event",
    useLottie: true,
    badge: "0",
  },
  {
    id: "home",
    name: "Home",
    icon: "home",
    useLottie: true,
    badge: "0",
  },
  {
    id: "vip",
    name: "Rewards",
    icon: "reward",
    useLottie: true,
  },
  {
    id: "profile",
    name: "Account",
    icon: "profile",
    useLottie: true,
    badge: "0",
  },
];

export const BottomNavigation: React.FC<BottomNavigationProps> = memo(
  ({
    className = "",
    activeItem = "home",
    onItemClick,
  }: BottomNavigationProps) => {
    const t = useT();
    const [vipBadgeCount] = useGlobalValue("vipBadgeCount");
    const isAuthenticated = useUserStore(state => state.isAuthenticated());

    // 划入动画状态
    const [visible, setVisible] = useState(false);
    const hasAnimated = useRef(false);

    // 首次加载后延迟触发划入动画
    useEffect(() => {
      if (hasAnimated.current) return;
      const timer = setTimeout(() => {
        setVisible(true);
        hasAnimated.current = true;
      }, 150);
      return () => clearTimeout(timer);
    }, []);

    // 异步获取 VIP Badge 数据；isAuthenticated 变化（登录/退出）时重新拉取
    useEffect(() => {
      if (!isAuthenticated) {
        setGlobalValue("vipBadgeCount", 0);
        return;
      }

      const fetchVipBadgeCount = async () => {
        try {
          const response = await getVipList();
          if (response?.data?.data.canClaimCount !== undefined) {
            setGlobalValue("vipBadgeCount", response.data.data.canClaimCount);
          }
        } catch (error) {
          console.error("Failed to fetch VIP badge count:", error);
        }
      };

      const timer = setTimeout(() => {
        fetchVipBadgeCount();
      }, 500);

      return () => clearTimeout(timer);
    }, [isAuthenticated]);

    const handleItemClick = useCallback(
      (item: NavigationItem) => {
        item.onClick?.();
        onItemClick?.(item.id);
      },
      [onItemClick]
    );

    const baseItems = defaultItems;

    const navigationItems = baseItems.map(item => {
      let translatedName = item.name;
      switch (item.id) {
        case "home":
          translatedName = t("navigation.home");
          break;
        case "event":
          translatedName = t("navigation.promotions");
          break;
        case "agent-center":
          translatedName = t("agent.invite");
          break;
        case "vip":
          translatedName = t("vip.rewards");
          break;
        case "profile":
          translatedName = t("navigation.account");
          break;
      }

      if (item.id === "vip") {
        return {
          ...item,
          name: translatedName,
          badge: vipBadgeCount > 0 ? String(vipBadgeCount) : undefined,
        };
      }
      return {
        ...item,
        name: translatedName,
        badge: undefined,
      };
    });

    const navClass = [
      "bottom-navigation",
      visible ? "bottom-navigation--visible" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <nav className={navClass}>
        <div className="bottom-navigation__container">
          {navigationItems.map(item => {
            const isActive = activeItem === item.id;

            return (
              <button
                key={item.id}
                className={["bottom-navigation__item", isActive ? "active" : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleItemClick(item)}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
              >
                <LottieIcon
                  iconId={item.id}
                  isActive={isActive}
                  className="bottom-navigation__lottie-icon"
                />
                <span className="bottom-navigation__label">{item.name}</span>
                {item.badge && item.badge !== "0" && (
                  <span className="bottom-navigation__badge">{item.badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export default BottomNavigation;
